import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Briefcase, FileText, Settings, LogOut, Menu, UserCircle, Calendar, CreditCard, MessageSquare, ShieldAlert, CalendarDays } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut, user, refreshProfile } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewBilling, setHasNewBilling] = useState(false);
  const [hasNewCalendar, setHasNewCalendar] = useState(false);
  const [hasNewJobs, setHasNewJobs] = useState(false);

  useEffect(() => {
    if (location.pathname === '/dashboard/billing') {
      localStorage.setItem('billing_last_viewed', Date.now().toString());
      setHasNewBilling(false);
    } else if (location.pathname === '/dashboard/calendar') {
      localStorage.setItem('calendar_last_viewed', Date.now().toString());
      setHasNewCalendar(false);
    } else if (location.pathname === '/dashboard/jobs') {
      localStorage.setItem('jobs_last_viewed', Date.now().toString());
      setHasNewJobs(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    
    const fetchNotifications = async () => {
      if (refreshProfile) refreshProfile();
      
      const [messagesRes, paymentsRes, bookingsRes, appsRes] = await Promise.all([
        supabase.from('messages').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('is_from_admin', true).eq('user_has_read', false),
        supabase.from('payments').select('id, created_at, status').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('bookings').select('id, created_at, status').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('applications').select('id, applied_at').eq('user_id', user.id).order('applied_at', { ascending: false }),
      ]);
      setUnreadCount(messagesRes.count || 0);

      // Check Billing dot
      const lastViewedBilling = parseInt(localStorage.getItem('billing_last_viewed') || '0', 10);
      const hasFailed = paymentsRes.data?.some(p => p.status === 'failed');
      const latestPaymentTime = paymentsRes.data?.[0] ? new Date(paymentsRes.data[0].created_at).getTime() : 0;
      setHasNewBilling(location.pathname !== '/dashboard/billing' && (hasFailed || latestPaymentTime > lastViewedBilling));

      // Check Calendar dot
      const lastViewedCalendar = parseInt(localStorage.getItem('calendar_last_viewed') || '0', 10);
      const latestBookingTime = bookingsRes.data?.[0] ? new Date(bookingsRes.data[0].created_at).getTime() : 0;
      setHasNewCalendar(location.pathname !== '/dashboard/calendar' && latestBookingTime > lastViewedCalendar);

      // Check Jobs dot
      const lastViewedJobs = parseInt(localStorage.getItem('jobs_last_viewed') || '0', 10);
      const latestJobTime = appsRes.data?.[0] ? new Date(appsRes.data[0].applied_at).getTime() : 0;
      setHasNewJobs(location.pathname !== '/dashboard/jobs' && latestJobTime > lastViewedJobs);
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user, location.pathname]);

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Career Profile', href: '/dashboard/profile', icon: UserCircle },
    { name: 'My Applications', href: '/dashboard/jobs', icon: Briefcase },
    { name: 'Document Vault', href: '/dashboard/documents', icon: FileText },
    { name: 'Calendar/Meetings', href: '/dashboard/calendar', icon: CalendarDays },
    { name: 'Messages', href: '/dashboard/messages', icon: MessageSquare },
    { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Get user initials from profile or email
  const getInitials = () => {
    const fn = (profile?.first_name || '').trim();
    const ln = (profile?.last_name || '').trim();
    if (fn && fn !== 'null' && fn !== 'Unknown') {
      return `${fn[0].toUpperCase()}${(ln && ln !== 'null' ? ln[0] : (fn[1] || '')).toUpperCase()}`;
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    const fn = (profile?.first_name || '').trim();
    const ln = (profile?.last_name || '').trim();
    if (fn && fn !== 'null' && fn !== 'Unknown') {
      return `${fn} ${ln && ln !== 'null' ? ln : ''}`.trim();
    }
    if (user?.email) {
      const prefix = user.email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return 'User';
  };

  const SidebarContent = () => (
    <>
      <div className="h-16 flex items-center px-6 border-b border-slate-800">
        <Link to="/" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
          <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold">Placement Point</span>
        </Link>
      </div>
      
      <div className="flex-1 py-6 px-4 space-y-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          // Determine dot indicator per tab
          let dotColor = null;
          if (item.name === 'Messages' && unreadCount > 0) dotColor = 'bg-rose-500';
          else if (item.name === 'Billing' && hasNewBilling) dotColor = 'bg-rose-500 animate-pulse';
          else if (item.name === 'Calendar/Meetings' && hasNewCalendar) dotColor = 'bg-emerald-500';
          else if (item.name === 'My Applications' && hasNewJobs) dotColor = 'bg-amber-400 animate-bounce';
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                isActive 
                  ? 'bg-indigo-600 text-white font-bold' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : ''}`} />
                {item.name}
              </div>
              {item.name === 'Messages' && unreadCount > 0 ? (
                <span className="flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full bg-rose-500 text-white">
                  {unreadCount}
                </span>
              ) : dotColor ? (
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
              ) : null}
            </Link>
          );
        })}
      </div>

      {/* Admin Panel Link in Sidebar */}
      {profile?.is_admin && (
        <div className="px-4 pb-2">
          <Link
            to="/admin"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300 transition-colors font-bold text-sm border border-amber-500/20"
          >
            <ShieldAlert className="w-5 h-5" />
            Admin Panel
          </Link>
        </div>
      )}

      {/* User info + Logout */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-sm">
              {getInitials()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{getDisplayName()}</p>
            <p className="text-xs text-slate-400 truncate capitalize">
              {(() => {
                const plan = profile?.subscription_plan;
                const isExpired = profile?.subscription_status === 'expired' || (profile?.subscription_expiry && new Date(profile.subscription_expiry) < new Date());
                const cleanPlan = (!plan || plan === 'none' || plan === 'placement_fee') ? 'Free' : plan;
                return isExpired ? `${cleanPlan} (Expired)` : cleanPlan;
              })()}
            </p>
          </div>
        </div>
        <button 
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-slate-900 text-white flex flex-col z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent />
      </div>

      {/* Desktop Sidebar */}
      <div className="w-64 bg-slate-900 text-white flex-col hidden md:flex">
        <SidebarContent />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Navbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="font-semibold text-slate-800 md:hidden">Placement Point</div>
          <div className="flex-1 hidden md:block" />
          <div className="flex items-center gap-4">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                {getInitials()}
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-5xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
